import { useCallback, useMemo, useReducer } from 'react'
import PropTypes from 'prop-types'

import { LOADING_STEP_ACTIONS, LOADING_STEPS } from '../utils/Constants/Constants'

import LoadingContext from './LoadingContextValue'

// Initial loading state
// Only blocking steps are tracked here. Non-blocking data loads in background:
// - ORGANISATION_USER: Role defaults to 'user', updates async
// - Manufacturing data: CUTTING_TECHS, MATERIALS, SHEETS, RATE_TABLES, RATES
const initialState = {
    steps: {
        [LOADING_STEPS.AUTHENTICATION]: {
            isLoading: false,
            isSuccess: false,
            error: null,
            errorMessage: null,
            errorObject: null,
        },
        [LOADING_STEPS.USER_CONTEXT]: {
            isLoading: false,
            isSuccess: false,
            error: null,
            errorMessage: null,
            errorObject: null,
        },
        [LOADING_STEPS.ORGANISATION]: {
            isLoading: false,
            isSuccess: false,
            error: null,
            errorMessage: null,
            errorObject: null,
        },
    },
    currentStep: 0,
    isComplete: false,
    hasErrors: false,
}

// Reducer function
const loadingReducer = (state, action) => {
    const { payload, type } = action

    switch (type) {
        case LOADING_STEP_ACTIONS.SET_STEP_LOADING: {
            const { stepKey } = payload
            const newSteps = {
                ...state.steps,
                [stepKey]: {
                    isLoading: true,
                    isSuccess: false,
                    error: null,
                },
            }

            return {
                ...state,
                steps: newSteps,
                ...calculateStepStatus(newSteps),
            }
        }

        case LOADING_STEP_ACTIONS.SET_STEP_SUCCESS: {
            const { stepKey } = payload
            const newSteps = {
                ...state.steps,
                [stepKey]: {
                    isLoading: false,
                    isSuccess: true,
                    error: null,
                },
            }

            return {
                ...state,
                steps: newSteps,
                ...calculateStepStatus(newSteps),
            }
        }

        case LOADING_STEP_ACTIONS.SET_STEP_ERROR: {
            const { error, errorObject, stepKey } = payload
            // Set all steps to not loading when an error occurs
            const newSteps = Object.keys(state.steps).reduce((acc, key) => {
                acc[key] = {
                    ...state.steps[key],
                    isLoading: false,
                }
                // Set error only on the specific step that failed
                if (key === stepKey) {
                    acc[key] = {
                        isLoading: false,
                        isSuccess: false,
                        error,
                        errorObject: errorObject || null,
                    }
                }
                return acc
            }, {})

            return {
                ...state,
                steps: newSteps,
                hasErrors: true,
                ...calculateStepStatus(newSteps),
            }
        }

        case LOADING_STEP_ACTIONS.SET_STEP_ERROR_MESSAGE: {
            const { errorMessage, stepKey } = payload
            const newSteps = {
                ...state.steps,
                [stepKey]: {
                    ...state.steps[stepKey],
                    errorMessage,
                },
            }

            return {
                ...state,
                steps: newSteps,
            }
        }

        //add a case for reset steps from a step onwards, mantain status for steps before
        case LOADING_STEP_ACTIONS.RESET_LOADING_FROM_STEP: {
            const { stepKey } = payload
            const stepKeys = Object.keys(state.steps)
            const stepIndex = stepKeys.indexOf(stepKey)

            const newSteps = Object.keys(state.steps).reduce((acc, key) => {
                const currentIndex = stepKeys.indexOf(key)

                // Keep previous steps unchanged
                if (currentIndex < stepIndex) {
                    acc[key] = { ...state.steps[key] }
                }
                // Reset steps from the specified step onwards
                else {
                    acc[key] = {
                        isLoading: false,
                        isSuccess: false,
                        error: null,
                        errorMessage: null,
                        errorObject: null,
                    }
                    // Set the specified step to loading
                    if (key === stepKey) {
                        acc[key].isLoading = true
                    }
                }
                return acc
            }, {})

            return {
                ...state,
                steps: newSteps,
                hasErrors: false, // Reset errors when restarting from a step
                ...calculateStepStatus(newSteps),
            }
        }

        case LOADING_STEP_ACTIONS.RESET_LOADING: {
            return initialState
        }

        default:
            return state
    }
}

// Calculate current step and completion status
// Only tracks which step is active - percentage display was removed
const calculateStepStatus = (steps) => {
    const stepKeys = Object.keys(steps)
    const loadingSteps = stepKeys.filter((key) => steps[key].isLoading)

    // Check if all steps are complete
    const isComplete = stepKeys.every((key) => steps[key]?.isSuccess)

    // Find the current active step (first loading or first incomplete)
    let currentStep = 0
    if (loadingSteps.length > 0) {
        currentStep = stepKeys.indexOf(loadingSteps[0])
    } else if (!isComplete) {
        const incompleteStepIndex = stepKeys.findIndex((key) => !steps[key].isSuccess)
        currentStep = incompleteStepIndex >= 0 ? incompleteStepIndex : 0
    }

    return {
        currentStep,
        isComplete,
    }
}

// Context is provided by LoadingContextValue.js

// Loading provider component
export const LoadingProvider = ({ children }) => {
    const [state, dispatch] = useReducer(loadingReducer, initialState)

    // Memoized action creators to prevent unnecessary re-renders
    const setStepLoading = useCallback(
        (stepKey) => {
            dispatch({ type: LOADING_STEP_ACTIONS.SET_STEP_LOADING, payload: { stepKey } })
        },
        [dispatch]
    )

    const setStepSuccess = useCallback(
        (stepKey) => {
            dispatch({ type: LOADING_STEP_ACTIONS.SET_STEP_SUCCESS, payload: { stepKey } })
        },
        [dispatch]
    )

    const setStepError = useCallback(
        (stepKey, error, errorObject = null) => {
            dispatch({ type: LOADING_STEP_ACTIONS.SET_STEP_ERROR, payload: { stepKey, error, errorObject } })
        },
        [dispatch]
    )

    const setStepErrorMessage = useCallback(
        (stepKey, errorMessage) => {
            dispatch({ type: LOADING_STEP_ACTIONS.SET_STEP_ERROR_MESSAGE, payload: { stepKey, errorMessage } })
        },
        [dispatch]
    )

    const resetLoadingFromStep = useCallback(
        (stepKey) => {
            dispatch({ type: LOADING_STEP_ACTIONS.RESET_LOADING_FROM_STEP, payload: { stepKey } })
        },
        [dispatch]
    )

    const resetLoading = useCallback(() => {
        dispatch({ type: LOADING_STEP_ACTIONS.RESET_LOADING })
    }, [dispatch])

    // Memoize the context value to prevent unnecessary re-renders
    const value = useMemo(
        () => ({
            ...state,
            setStepLoading,
            setStepSuccess,
            setStepError,
            setStepErrorMessage,
            resetLoading,
            resetLoadingFromStep,
        }),
        [state, setStepLoading, setStepSuccess, setStepError, setStepErrorMessage, resetLoading, resetLoadingFromStep]
    )

    return <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
}

LoadingProvider.propTypes = {
    children: PropTypes.node.isRequired,
}

// Custom hook to use loading context
// Note: use the dedicated hook at '@/common/hooks/useLoadingContext'
